import {
  BuildingOfficeIcon,
  CalendarDateRangeIcon,
  MapPinIcon,
  UsersIcon,
} from '@heroicons/react/24/outline';
import type { ReactNode } from 'react';
import type { ProviderCategory } from '~/types';
import type { MsaasJobListingResponse } from '../types';

/**
 * [MCV-1958] The facts a B2B result card shows under its title, shared by the Selling list
 * (`ListingsList`) and the Buying list (`OfferingListingsList`) so the two cards cannot drift
 * apart again: the Provider Type / Specialty rows, the 2x2 icon grid and the price line.
 */

export const TextWithIcon = ({
  text,
  icon,
  cyTag,
}: {
  text: string;
  icon: ReactNode;
  cyTag: string;
}) => {
  return (
    <div className="flex min-w-0 items-start gap-2" data-cy={cyTag}>
      <div className="mt-0.5 shrink-0">{icon}</div>
      <p className="text-sm font-normal leading-6 text-content-secondary [overflow-wrap:anywhere]">
        {text}
      </p>
    </div>
  );
};

export const dateToMonth = (date: string) => {
  return new Date(date).toLocaleDateString('en-US', {
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  });
};

/** The `data-cy` of each fact. The caller supplies them (index suffix included) so the two lists
 * keep their own tags. */
export type ListingCardCyTags = {
  states: string;
  ftes: string;
  dates: string;
  workLocation: string;
  contractValue: string;
};

const DefinitionRow = ({ term, children }: { term: string; children: ReactNode }) => (
  <div className="flex items-baseline gap-2">
    <dt className="shrink-0 whitespace-nowrap text-sm font-semibold leading-5 text-content-secondary">
      {term}
    </dt>
    <dd className="min-w-0 text-[1rem] font-normal leading-6 text-content-primary [overflow-wrap:anywhere]">
      {children}
    </dd>
  </div>
);

export const ListingProviderTypeAndSpecialty = ({
  listing,
  categories,
}: {
  listing: MsaasJobListingResponse;
  categories: ProviderCategory[];
}) => (
  <dl className="mb-2 space-y-1">
    <DefinitionRow term="Provider Type:">
      {categories.find((category) => category.id === listing.providerCategoryId)
        ?.providerCategoryCode ||
        (listing.providerCategoryId != null ? `Category ${listing.providerCategoryId}` : '—')}
    </DefinitionRow>
    <DefinitionRow term="Specialty:">{listing.specialtyName || '—'}</DefinitionRow>
  </dl>
);

export const ListingIconGrid = ({
  listing,
  cyTags,
}: {
  listing: MsaasJobListingResponse;
  cyTags: Pick<ListingCardCyTags, 'states' | 'ftes' | 'dates' | 'workLocation'>;
}) => (
  <div className="grid grid-cols-2 gap-x-3 gap-y-1">
    <TextWithIcon
      cyTag={cyTags.states}
      text={
        (listing?.stateCodes?.length ?? 0) > 3
          ? `${listing.stateCodes?.length ?? 0} states`
          : // [MCV-1546] join() on an empty array renders a bare
            // label next to the pin icon; fall back to the same
            // em dash the neighbouring tiles use.
            listing?.stateCodes?.join(', ') || '—'
      }
      icon={<MapPinIcon className="size-5 text-grey-400" />}
    />
    <TextWithIcon
      cyTag={cyTags.ftes}
      text={`${listing.detail?.numOfFtes ?? '—'} FTEs`}
      icon={<UsersIcon className="size-5 text-grey-400" />}
    />
    <TextWithIcon
      cyTag={cyTags.dates}
      text={
        listing.detail?.startDate
          ? `${dateToMonth(listing.detail.startDate)}${listing.detail?.endDate ? ' - ' + dateToMonth(listing.detail.endDate) : '+'}`
          : 'TBD'
      }
      icon={<CalendarDateRangeIcon className="size-5 text-grey-400" />}
    />
    <TextWithIcon
      cyTag={cyTags.workLocation}
      text={
        listing.workLocation === 'ONSITE'
          ? 'Onsite'
          : listing.workLocation === 'REMOTE'
            ? 'Remote'
            : 'Hybrid'
      }
      icon={<BuildingOfficeIcon className="size-5 text-grey-400" />}
    />
  </div>
);

export const ListingPriceLine = ({
  listing,
  cyTag,
  aside,
}: {
  listing: MsaasJobListingResponse;
  cyTag: string;
  /** Right-aligned on the same line; the Buying card puts its roster count here. */
  aside?: ReactNode;
}) => {
  const totalContractValue = listing.detail?.totalContractValue;
  const formattedContractValue =
    totalContractValue != null
      ? `$${Number(totalContractValue).toLocaleString('en-US', {
          maximumFractionDigits: 0,
        })}`
      : '—';
  return (
    <div className="mt-2 flex items-baseline gap-2">
      <p className="text-[1rem] font-semibold leading-6 text-primary-50">
        {listing.detail?.pricePerHour != null ? `$${listing.detail.pricePerHour}/hr` : '—'}
      </p>
      <p className="text-sm font-normal leading-6 text-content-secondary" data-cy={cyTag}>
        {formattedContractValue === '—'
          ? formattedContractValue
          : `${formattedContractValue} total`}
      </p>
      {aside}
    </div>
  );
};

/**
 * Provider Type / Specialty, the icon grid and the price line, in the order a Selling card has
 * them. `afterGrid` sits between the grid and the price line (the Buying card's location note).
 */
export const ListingCardFacts = ({
  listing,
  categories,
  cyTags,
  afterGrid,
  priceAside,
}: {
  listing: MsaasJobListingResponse;
  categories: ProviderCategory[];
  cyTags: ListingCardCyTags;
  afterGrid?: ReactNode;
  priceAside?: ReactNode;
}) => (
  <>
    <ListingProviderTypeAndSpecialty listing={listing} categories={categories} />
    <ListingIconGrid listing={listing} cyTags={cyTags} />
    {afterGrid}
    <ListingPriceLine listing={listing} cyTag={cyTags.contractValue} aside={priceAside} />
  </>
);
